/**
 * Cold Outreach MJML Email Templates
 * Designed for maximum deliverability and response rates
 *
 * Design principles:
 * - Plain-text appearance (minimal HTML styling)
 * - Short and punchy (under 150 words)
 * - Personal signature with photo
 * - Mobile-first responsive design
 * - CAN-SPAM compliant footer
 */

// ============================================================================
// Common Components
// ============================================================================

export const COLD_OUTREACH_SIGNATURE = `
    <mj-section padding="20px 0 0 0">
      <mj-column>
        <mj-text font-size="14px" color="#333333" line-height="1.4">
          Best,<br/>
          {{sender_name}}<br/>
          <span style="color: #666666;">{{sender_title}}</span>
        </mj-text>
      </mj-column>
    </mj-section>
`;

export const COLD_OUTREACH_FOOTER = `
    <mj-section padding="30px 20px 20px 20px">
      <mj-column>
        <mj-divider border-color="#e0e0e0" border-width="1px" padding="0 0 20px 0" />
        <mj-text font-size="11px" color="#999999" line-height="1.4" align="center">
          {{sender_company}} · {{company_address}}<br/>
          <a href="{{unsubscribe_url}}" style="color: #999999;">Unsubscribe</a> · <a href="{{preferences_url}}" style="color: #999999;">Email Preferences</a>
        </mj-text>
      </mj-column>
    </mj-section>
`;

// ============================================================================
// Prospecting Templates
// ============================================================================

export const coldOutreachProblemAware = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          {{personalized_opener}}
        </mj-text>
        <mj-text padding-top="15px">
          I noticed {{company_name}} is {{observation}}. Many companies in {{industry}} are dealing with {{pain_point}} right now.
        </mj-text>
        <mj-text padding-top="15px">
          We've helped companies like {{case_study_company}} {{case_study_result}}.
        </mj-text>
        <mj-text padding-top="15px">
          Would you be open to a 15-minute call to see if we might be able to help?
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

export const coldOutreachValueFirst = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          I came across {{company_name}} and thought you might find this useful: <a href="{{resource_url}}" style="color: #3498db;">{{resource_name}}</a>
        </mj-text>
        <mj-text padding-top="15px">
          It covers {{resource_benefit}} - something I know is top of mind for {{job_title}}s right now.
        </mj-text>
        <mj-text padding-top="15px">
          No strings attached - just thought it might help.
        </mj-text>
        <mj-text padding-top="15px">
          If you'd like to discuss how {{company_name}} could apply these insights, I'm happy to chat.
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

export const coldOutreachTriggerBased = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          Congrats on {{trigger_event}}! That's exciting news for {{company_name}}.
        </mj-text>
        <mj-text padding-top="15px">
          When companies hit this milestone, they often start thinking about {{related_challenge}}. It's usually the right time to {{solution_hint}}.
        </mj-text>
        <mj-text padding-top="15px">
          We recently helped {{case_study_company}} through a similar transition - they {{case_study_result}}.
        </mj-text>
        <mj-text padding-top="15px">
          Would a quick call make sense?
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

export const coldOutreachCompetitorDisplacement = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          I noticed {{company_name}} is using {{competitor_name}}. We've been hearing from a lot of their customers recently.
        </mj-text>
        <mj-text padding-top="15px">
          Common frustrations: {{competitor_limitation_1}} and {{competitor_limitation_2}}.
        </mj-text>
        <mj-text padding-top="15px">
          {{switcher_company}} switched to us and saw {{switcher_result}} in the first 90 days.
        </mj-text>
        <mj-text padding-top="15px">
          Curious if any of this resonates? Happy to share what made the difference for them.
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

// ============================================================================
// Follow-up Templates
// ============================================================================

export const coldOutreachFollowUp = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          Quick follow up on my note from {{time_since_last_email}}.
        </mj-text>
        <mj-text padding-top="15px">
          I wanted to add one thing: {{new_value_add}}
        </mj-text>
        <mj-text padding-top="15px">
          Worth a quick conversation?
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

export const coldOutreachSocialProof = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          I wanted to share what {{testimonial_company}} said after working with us:
        </mj-text>
        <mj-text padding-top="15px" font-style="italic" color="#555555">
          "{{testimonial_quote}}"
        </mj-text>
        <mj-text padding-top="10px" font-size="12px" color="#888888">
          — {{testimonial_author}}, {{testimonial_title}}
        </mj-text>
        <mj-text padding-top="15px">
          They're in {{testimonial_industry}}, similar to {{company_name}}. Happy to share how we achieved this.
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

export const coldOutreachBreakup = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          I've reached out a few times and haven't heard back. I'll take the hint!
        </mj-text>
        <mj-text padding-top="15px">
          Before I close your file, I wanted to leave you with this: <a href="{{final_resource_url}}" style="color: #3498db;">{{final_resource_name}}</a>
        </mj-text>
        <mj-text padding-top="15px">
          If timing changes, my door's always open.
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

// ============================================================================
// Meeting Request Templates
// ============================================================================

export const coldOutreachDirectAsk = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          I'd love 15 minutes to show you how {{value_proposition}}.
        </mj-text>
        <mj-text padding-top="15px">
          Companies like {{case_study_company}} have seen {{case_study_result}} using our approach.
        </mj-text>
        <mj-button href="{{calendar_link}}" background-color="#3498db" color="#ffffff" border-radius="4px" font-size="14px" padding="20px 0">
          Pick a Time
        </mj-button>
        <mj-text font-size="12px" color="#888888" align="center">
          Or reply with a time that works better
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

export const coldOutreachCalendarLink = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          Here's my calendar: <a href="{{calendar_link}}" style="color: #3498db;">{{calendar_link_text}}</a>
        </mj-text>
        <mj-text padding-top="15px">
          Pick any time that works. Looking forward to connecting.
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

// ============================================================================
// Event-Based Templates
// ============================================================================

export const coldOutreachWebinarInvite = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          We're hosting a live session on <strong>{{webinar_title}}</strong> - thought you might be interested.
        </mj-text>
        <mj-text padding-top="15px">
          <strong>When:</strong> {{webinar_date}} at {{webinar_time}}<br/>
          <strong>What you'll learn:</strong><br/>
          • {{agenda_point_1}}<br/>
          • {{agenda_point_2}}<br/>
          • {{agenda_point_3}}
        </mj-text>
        <mj-button href="{{registration_url}}" background-color="#3498db" color="#ffffff" border-radius="4px" font-size="14px" padding="20px 0">
          Save Your Spot
        </mj-button>
        <mj-text font-size="12px" color="#888888">
          Can't make it live? Register anyway and we'll send you the recording.
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

export const coldOutreachContentOffer = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          Just published: <a href="{{content_url}}" style="color: #3498db; font-weight: bold;">{{content_title}}</a>
        </mj-text>
        <mj-text padding-top="15px">
          It covers {{content_summary}} - based on data from {{data_source}}.
        </mj-text>
        <mj-text padding-top="15px">
          Key insight: {{key_insight}}
        </mj-text>
        <mj-text padding-top="15px">
          Thought it might be valuable for {{company_name}}.
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

// ============================================================================
// ABM Templates
// ============================================================================

export const coldOutreachExecutive = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          {{personalized_observation}}
        </mj-text>
        <mj-text padding-top="15px">
          I've been speaking with {{industry}} leaders about {{strategic_topic}}, and wanted to share a perspective.
        </mj-text>
        <mj-text padding-top="15px">
          Would a brief executive briefing be valuable?
        </mj-text>
      </mj-column>
    </mj-section>
    <mj-section padding="20px 0 0 0">
      <mj-column>
        <mj-text font-size="14px" color="#333333" line-height="1.4">
          {{sender_name}}<br/>
          <span style="color: #666666;">{{sender_title}}, {{sender_company}}</span>
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

export const coldOutreachCustomProposal = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          I put together a custom proposal specifically for {{company_name}}: <a href="{{proposal_url}}" style="color: #3498db; font-weight: bold;">{{proposal_title}}</a>
        </mj-text>
        <mj-text padding-top="15px">
          Based on my research, I believe you could:
        </mj-text>
        <mj-text padding-top="10px" padding-left="20px">
          • {{outcome_1}}<br/>
          • {{outcome_2}}<br/>
          • {{outcome_3}}
        </mj-text>
        <mj-text padding-top="15px">
          Would you have time this week to walk through it together?
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

// ============================================================================
// Re-engagement Templates
// ============================================================================

export const coldOutreachLostDeal = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          It's been {{time_since}} since we last spoke. A lot has changed on our end.
        </mj-text>
        <mj-text padding-top="15px">
          We've since launched {{new_feature_1}} and {{new_feature_2}} - two things I remember being important to you.
        </mj-text>
        <mj-text padding-top="15px">
          Would it be worth a fresh look?
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

export const coldOutreachWinback = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          We miss you at {{product_name}}!
        </mj-text>
        <mj-text padding-top="15px">
          Since you left, we've completely rebuilt {{improvement_area}}. Here's what's new:
        </mj-text>
        <mj-text padding-top="10px" padding-left="20px">
          • {{improvement_1}}<br/>
          • {{improvement_2}}<br/>
          • {{improvement_3}}
        </mj-text>
        <mj-text padding-top="15px">
          We'd love for you to give us another try. Here's {{return_offer}} to welcome you back.
        </mj-text>
        <mj-button href="{{return_url}}" background-color="#3498db" color="#ffffff" border-radius="4px" font-size="14px" padding="20px 0">
          Come Back
        </mj-button>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

// ============================================================================
// Industry-Specific Templates
// ============================================================================

export const coldOutreachSaaS = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          Scaling customer support while keeping NRR strong - it's the challenge every SaaS company faces.
        </mj-text>
        <mj-text padding-top="15px">
          {{saas_case_study_company}} (Series {{funding_stage}}) used our AI chatbot to handle {{percentage}}% of support volume automatically, freeing their team to focus on retention.
        </mj-text>
        <mj-text padding-top="15px">
          Result: {{saas_result}}
        </mj-text>
        <mj-text padding-top="15px">
          Curious how this might work for {{company_name}}?
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

export const coldOutreachEcommerce = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          {{abandonment_stat}}% of carts are abandoned. But here's the thing - many of those shoppers just have questions.
        </mj-text>
        <mj-text padding-top="15px">
          {{ecom_case_study}} added our AI chat to answer product questions in real-time. Result: {{ecom_result}}.
        </mj-text>
        <mj-text padding-top="15px">
          With {{peak_season}} coming up, could be worth a look?
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

// ============================================================================
// Partner Templates
// ============================================================================

export const coldOutreachPartner = `
<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, Helvetica, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.6" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#ffffff">
    <mj-section padding="30px 20px 0 20px">
      <mj-column>
        <mj-text>
          Hi {{first_name}},
        </mj-text>
        <mj-text padding-top="15px">
          {{company_name}} caught my eye as a potential partner.
        </mj-text>
        <mj-text padding-top="15px">
          We're building a partner program for agencies who want to offer AI-powered customer engagement to their clients. Here's what partners get:
        </mj-text>
        <mj-text padding-top="10px" padding-left="20px">
          • {{partner_benefit_1}}<br/>
          • {{partner_benefit_2}}<br/>
          • {{partner_benefit_3}}
        </mj-text>
        <mj-text padding-top="15px">
          {{partner_reference}} added ${"$"}{{partner_mrr}} MRR through the program. Interested in learning more?
        </mj-text>
      </mj-column>
    </mj-section>
    ${COLD_OUTREACH_SIGNATURE}
    ${COLD_OUTREACH_FOOTER}
  </mj-body>
</mjml>
`;

// ============================================================================
// Template Metadata & Exports
// ============================================================================

const problemAwareTemplate = {
  id: "coldOutreach-problemAware",
  name: "Problem-Aware Opener",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Lead with the prospect's pain point, not your product. Ideal for initial cold outreach.",
  features: ["Pain point focus", "Case study reference", "Simple CTA", "Plain-text style"],
  subject: "{{first_name}}, quick question about {{pain_point}}",
  subTitle: "Problem-aware cold outreach",
  mjmlTemplate: coldOutreachProblemAware,
  tags: ["cold-outreach", "prospecting", "problem-aware", "b2b"],
};

const valueFirstTemplate = {
  id: "coldOutreach-valueFirst",
  name: "Value-First Approach",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Lead with a valuable resource before asking for anything. Builds trust and credibility.",
  features: ["Free resource", "No strings attached", "Soft CTA", "Trust building"],
  subject: "{{first_name}}, thought you'd find this useful",
  subTitle: "Value-first cold outreach",
  mjmlTemplate: coldOutreachValueFirst,
  tags: ["cold-outreach", "prospecting", "value-first", "content"],
};

const triggerBasedTemplate = {
  id: "coldOutreach-triggerBased",
  name: "Trigger-Based Outreach",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Leverage a trigger event (funding, hiring, product launch) for timely outreach.",
  features: ["Trigger event hook", "Timely relevance", "Case study proof", "Clear CTA"],
  subject: "Congrats on {{trigger_event}}, {{first_name}}!",
  subTitle: "Event-triggered cold outreach",
  mjmlTemplate: coldOutreachTriggerBased,
  tags: ["cold-outreach", "prospecting", "trigger-based", "funding"],
};

const competitorTemplate = {
  id: "coldOutreach-competitor",
  name: "Competitor Displacement",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Target users of competitor products with a switching story.",
  features: ["Competitor reference", "Pain acknowledgment", "Switch success story", "Soft approach"],
  subject: "{{first_name}}, thoughts on {{competitor_name}}?",
  subTitle: "Competitor displacement",
  mjmlTemplate: coldOutreachCompetitorDisplacement,
  tags: ["cold-outreach", "prospecting", "competitive", "displacement"],
};

const followUpTemplate = {
  id: "coldOutreach-followUp",
  name: "Value-Add Follow-Up",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Follow up with new value instead of just 'checking in'.",
  features: ["New value addition", "Brief format", "Non-pushy", "Clear ask"],
  subject: "Re: {{original_subject}}",
  subTitle: "Value-add follow-up",
  mjmlTemplate: coldOutreachFollowUp,
  tags: ["cold-outreach", "followup", "value-add", "nurture"],
};

const socialProofTemplate = {
  id: "coldOutreach-socialProof",
  name: "Social Proof Email",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Build credibility with customer testimonials and results.",
  features: ["Customer quote", "Specific results", "Industry relevance", "Trust building"],
  subject: "What {{testimonial_company}} said about us",
  subTitle: "Testimonial-focused follow-up",
  mjmlTemplate: coldOutreachSocialProof,
  tags: ["cold-outreach", "followup", "social-proof", "testimonial"],
};

const breakupTemplate = {
  id: "coldOutreach-breakup",
  name: "Breakup Email",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Final email that often gets the highest response rate.",
  features: ["Closing the file", "Final resource", "Door left open", "No guilt"],
  subject: "Closing the loop, {{first_name}}",
  subTitle: "Final breakup email",
  mjmlTemplate: coldOutreachBreakup,
  tags: ["cold-outreach", "followup", "breakup", "final"],
};

const directAskTemplate = {
  id: "coldOutreach-directAsk",
  name: "Direct Meeting Request",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Simple, direct ask for a meeting with calendar link.",
  features: ["Clear value prop", "Calendar button", "Social proof", "Short format"],
  subject: "15 min this week, {{first_name}}?",
  subTitle: "Direct meeting request",
  mjmlTemplate: coldOutreachDirectAsk,
  tags: ["cold-outreach", "meeting", "direct-ask", "calendar"],
};

const calendarLinkTemplate = {
  id: "coldOutreach-calendarLink",
  name: "Frictionless Calendar",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Ultra-minimal email with just a calendar link.",
  features: ["Minimal text", "Calendar focus", "Low friction", "Mobile-friendly"],
  subject: "{{first_name}}, pick a time",
  subTitle: "Calendar-focused email",
  mjmlTemplate: coldOutreachCalendarLink,
  tags: ["cold-outreach", "meeting", "calendar", "minimal"],
};

const webinarTemplate = {
  id: "coldOutreach-webinarInvite",
  name: "Webinar Invitation",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Invite prospects to a webinar with agenda and registration link.",
  features: ["Event details", "Agenda preview", "Registration CTA", "Recording mention"],
  subject: "[Webinar] {{webinar_title}} - {{webinar_date}}",
  subTitle: "Webinar invitation",
  mjmlTemplate: coldOutreachWebinarInvite,
  tags: ["cold-outreach", "event", "webinar", "registration"],
};

const contentOfferTemplate = {
  id: "coldOutreach-contentOffer",
  name: "Content Offer",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Share valuable content to build trust and engagement.",
  features: ["Content preview", "Key insight", "No hard sell", "Value-first"],
  subject: "Free guide: {{content_title}}",
  subTitle: "Content offer email",
  mjmlTemplate: coldOutreachContentOffer,
  tags: ["cold-outreach", "event", "content", "lead-gen"],
};

const executiveTemplate = {
  id: "coldOutreach-executive",
  name: "Executive Outreach",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Peer-to-peer executive outreach with strategic positioning.",
  features: ["Peer tone", "Strategic focus", "Brief format", "Executive signature"],
  subject: "{{first_name}}, quick thought on {{strategic_topic}}",
  subTitle: "C-level outreach",
  mjmlTemplate: coldOutreachExecutive,
  tags: ["cold-outreach", "abm", "executive", "strategic"],
};

const customProposalTemplate = {
  id: "coldOutreach-customProposal",
  name: "Custom Proposal",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Present a tailored proposal with specific outcomes.",
  features: ["Custom research", "Specific outcomes", "Proposal link", "Meeting ask"],
  subject: "Custom proposal for {{company_name}}",
  subTitle: "Tailored proposal email",
  mjmlTemplate: coldOutreachCustomProposal,
  tags: ["cold-outreach", "abm", "proposal", "enterprise"],
};

const lostDealTemplate = {
  id: "coldOutreach-lostDeal",
  name: "Lost Deal Revival",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Re-engage prospects from deals that were lost or went cold.",
  features: ["What's changed", "New features", "Fresh look ask", "Non-defensive"],
  subject: "{{first_name}}, a lot has changed since we last talked",
  subTitle: "Lost deal re-engagement",
  mjmlTemplate: coldOutreachLostDeal,
  tags: ["cold-outreach", "reengagement", "lost-deal", "revival"],
};

const winbackTemplate = {
  id: "coldOutreach-winback",
  name: "Win-Back Email",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Re-engage churned customers with improvements and offers.",
  features: ["Improvement list", "Return offer", "CTA button", "Positive tone"],
  subject: "We miss you at {{product_name}}!",
  subTitle: "Customer win-back",
  mjmlTemplate: coldOutreachWinback,
  tags: ["cold-outreach", "reengagement", "winback", "churned"],
};

const saasTemplate = {
  id: "coldOutreach-saas",
  name: "SaaS Company Outreach",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Tailored messaging for B2B SaaS companies.",
  features: ["SaaS metrics", "NRR focus", "Stage-specific", "Quick case study"],
  subject: "{{first_name}}, scaling support without scaling headcount",
  subTitle: "SaaS-focused outreach",
  mjmlTemplate: coldOutreachSaaS,
  tags: ["cold-outreach", "industry", "saas", "b2b"],
};

const ecommerceTemplate = {
  id: "coldOutreach-ecommerce",
  name: "E-commerce Outreach",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Revenue-focused messaging for e-commerce brands.",
  features: ["Cart abandonment focus", "Revenue metrics", "Peak season tie-in", "Quick win"],
  subject: "{{first_name}}, turning support into sales",
  subTitle: "E-commerce focused outreach",
  mjmlTemplate: coldOutreachEcommerce,
  tags: ["cold-outreach", "industry", "ecommerce", "dtc"],
};

const partnerTemplate = {
  id: "coldOutreach-partner",
  name: "Partner Recruitment",
  category: "coldOutreach",
  purpose: "campaign" as const,
  description: "Recruit agencies and partners to your partner program.",
  features: ["Partner benefits", "Revenue opportunity", "Success reference", "Clear value"],
  subject: "{{first_name}}, partnership opportunity for {{company_name}}",
  subTitle: "Partner recruitment email",
  mjmlTemplate: coldOutreachPartner,
  tags: ["cold-outreach", "partner", "agency", "recruitment"],
};

// Combined exports
export const COLD_OUTREACH_TEMPLATES = {
  problemAware: problemAwareTemplate,
  valueFirst: valueFirstTemplate,
  triggerBased: triggerBasedTemplate,
  competitor: competitorTemplate,
  followUp: followUpTemplate,
  socialProof: socialProofTemplate,
  breakup: breakupTemplate,
  directAsk: directAskTemplate,
  calendarLink: calendarLinkTemplate,
  webinarInvite: webinarTemplate,
  contentOffer: contentOfferTemplate,
  executive: executiveTemplate,
  customProposal: customProposalTemplate,
  lostDeal: lostDealTemplate,
  winback: winbackTemplate,
  saas: saasTemplate,
  ecommerce: ecommerceTemplate,
  partner: partnerTemplate,
};

export const COLD_OUTREACH_TEMPLATES_ARRAY = [
  problemAwareTemplate,
  valueFirstTemplate,
  triggerBasedTemplate,
  competitorTemplate,
  followUpTemplate,
  socialProofTemplate,
  breakupTemplate,
  directAskTemplate,
  calendarLinkTemplate,
  webinarTemplate,
  contentOfferTemplate,
  executiveTemplate,
  customProposalTemplate,
  lostDealTemplate,
  winbackTemplate,
  saasTemplate,
  ecommerceTemplate,
  partnerTemplate,
];

export default {
  templates: COLD_OUTREACH_TEMPLATES,
  templatesArray: COLD_OUTREACH_TEMPLATES_ARRAY,
  getById: (id: string) => COLD_OUTREACH_TEMPLATES_ARRAY.find((t) => t.id === id),
  getByTag: (tag: string) => COLD_OUTREACH_TEMPLATES_ARRAY.filter((t) => t.tags.includes(tag)),
};
